import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from '@/lib/api-error'
import { goToLoginApp } from '@/lib/login-app-url'
import { clearTokens, isJustLoggedIn } from '@/lib/token-storage'

function handleUnauthorizedQueryError(error: unknown): boolean {
  if (isJustLoggedIn()) return false

  const status =
    error instanceof ApiError
      ? error.statusCode
      : typeof error === 'object' && error !== null && 'status' in error
        ? Number((error as { status?: unknown }).status)
        : undefined

  if (status !== 401) return false

  clearTokens()
  goToLoginApp()
  return true
}

export function getContext() {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        handleUnauthorizedQueryError(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        handleUnauthorizedQueryError(error)
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutos - dados considerados "frescos"
        gcTime: 1000 * 60 * 30, // 30 minutos - tempo no cache (antigo cacheTime)
        retry: (failureCount, error) => {
          if (handleUnauthorizedQueryError(error)) return false
          return failureCount < 1
        },
        refetchOnWindowFocus: false, // Não refetch automaticamente ao focar na janela
      },
    },
  })
  return {
    queryClient,
  }
}

export function Provider({
  children,
  queryClient,
}: {
  children: React.ReactNode
  queryClient: QueryClient
}) {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
